import {
  Box,
  Typography,
  Grid,
  TextareaAutosize,
  Button,
  CircularProgress,
} from "@mui/material";
import type { RootState } from "../../../../../store/Index";
import apiService from "../../../../../service/Api";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useCallback, useEffect, useRef, useState } from "react";
import BookIcon from "@mui/icons-material/Book";
import { User } from "../../../../users-admin/types/User";
import { MemoryBook } from "../../../../../types/memory-table";
import { MemoryBookTableRow } from "./MemoryBookTableRow";
import { CurrentMemoryBookInfo } from "./CurrentMemoryBookInfo";
import { ReactComponent as CloudIcon } from "./../../../../../assets/cloud-icon.svg";

export const MemoryTableUpload: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedBook, setSelectedBook] = useState<File | null>(null);
  const [canvaLink, setCanvaLink] = useState<string>("");
  const [uploadNotes, setUploadNotes] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [memoryBooks, setMemoryBooks] = useState<MemoryBook[]>([]);
  const [uploadUser, setUploadUser] = useState<User | null>(null);

  const fetchMemoryBooks = useCallback(async () => {
    setIsLoading(true);
    setError(false);

    try {
      const response = await apiService.get<{ results: MemoryBook[] }>(
        `/api/streams/memory-book/?stream=${id}`
      );

      setMemoryBooks(response.data.results || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load comments. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchMemoryBooks();
  }, [fetchMemoryBooks]);

  const fetchUser = useCallback(async () => {
    try {
      const user = memoryBooks[memoryBooks.length - 1].created_by;
      const response = await apiService.get<User>(
        `/api/authentication/users/${user}/`
      );
      setUploadUser(response.data);
    } catch (err) {
      console.error(err);
    }
  }, [memoryBooks]);

  useEffect(() => {
    if (memoryBooks.length > 0) {
      const memoryBook = memoryBooks[0];
      setCanvaLink(memoryBook.canva_memory_book_link || "");
      setUploadNotes(memoryBook.notes || "");
      fetchUser();
    }
  }, [fetchUser, memoryBooks]);

  const browseFile = () => {
    fileInputRef.current?.click();
  };

  const onUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files: FileList | null = e.target.files;

    if (files === null || files.length === 0) {
      toast.warn("No book found,please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    }

    if (files && files.length > 0) {
      setSelectedBook(files[0]);
      return;
    }

    if (files && files.length > 1) {
      toast.warn("Please select one book.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    }
  };

  const updateCurrentBook = async () => {
    setIsLoading(true);

    try {
      const currentBook: MemoryBook = memoryBooks[0];

      const formData = new FormData();
      formData.append("canva_memory_book_link", canvaLink);
      formData.append("notes", uploadNotes);

      await apiService.patch(
        `/api/streams/memory-book/${currentBook.id}/`,
        formData
      );

      toast.success("Detailed updated successfully.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to update details.Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const uploadBook = async () => {
    if (selectedBook === null) {
      toast.warn(`You have not selected any book`, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      return;
    }

    setIsUploading(true);

    const formData = new FormData();
    formData.append("file", selectedBook);
    formData.append("stream_id", `${id}`);
    formData.append("canva_memory_book_link", canvaLink);
    formData.append("notes", uploadNotes || "No notes");

    try {
      await apiService.post(`/api/streams/memory-book/`, formData);

      toast.success(`${selectedBook.name} has been uploaded `, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      fetchMemoryBooks();
      setSelectedBook(null);
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload book.Please try again", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box bgcolor={reduxTheme.primary_colors[900]} p={2} borderRadius={2}>
      <Typography variant="h5" fontWeight={"bold"}>
        Book and Upload
      </Typography>

      <Grid container spacing={2} mt={3} mb={2}>
        <Grid size={6}>
          <Box
            width={"100%"}
            height={310}
            borderRadius={2}
            border={`1px solid #CBD5E1`}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            flexDirection={"column"}
          >
            {selectedBook === null && (
              <>
                <CloudIcon
                  style={{
                    height: 60,
                    width: 60,
                  }}
                />
                <Typography variant="body1" textAlign={"center"} mb={1}>
                  Drag and drop your pdf here (max size = 25mb)
                  <br /> or
                </Typography>
              </>
            )}

            {selectedBook && (
              <>
                <BookIcon
                  sx={{
                    height: 50,
                    width: 50,
                    color: "#CBD5E1",
                  }}
                />
                <Typography variant="body1" textAlign={"center"} mb={1}>
                  {selectedBook.name}
                </Typography>
              </>
            )}

            <input
              onChange={(e) => onUploadChange(e)}
              accept=".pdf"
              ref={fileInputRef}
              type="file"
              style={{ display: "none" }}
            />
            <Button onClick={browseFile} variant="contained">
              Browse file
            </Button>
          </Box>
        </Grid>

        <Grid size={6}>
          <Typography variant="body1" sx={{ fontWeight: 600 }} mb={2}>
            Canva Link
          </Typography>

          <TextareaAutosize
            aria-placeholder="Add any special instructions or notes"
            value={canvaLink}
            placeholder="Paste canva link here"
            style={{
              width: "100%",
              resize: "none",
              padding: "0.5rem",
              height: 50,
              maxHeight: 50,
              borderRadius: 4,
              backgroundColor: "#F0F4F8",
              border: "none",
              marginBottom: "1.3rem",
            }}
          />

          <Typography variant="h5" sx={{ fontWeight: 600 }} mb={2}>
            Notes
          </Typography>
          <Box sx={{ height: 162, position: "relative" }}>
            <TextareaAutosize
              value={uploadNotes}
              onChange={(e) => setUploadNotes(e.target.value)}
              aria-placeholder="Add any special instructions or notes"
              placeholder="Add any special instructions or notes"
              style={{
                width: "100%",
                resize: "none",
                padding: "0.5rem",
                height: 149,
                maxHeight: 162,
                borderRadius: 4,
                backgroundColor: "#F0F4F8",
                border: "none",
              }}
            />
            <Button
              variant="contained"
              disabled={isUploading}
              onClick={selectedBook ? uploadBook : updateCurrentBook}
              sx={{ position: "absolute", right: ".7em", bottom: ".8em" }}
            >
              {isUploading ? (
                <CircularProgress size={20} />
              ) : selectedBook ? (
                "Upload"
              ) : (
                "Update"
              )}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <CurrentMemoryBookInfo
        memoryBooks={memoryBooks}
        isLoading={isLoading}
        error={error}
        uploadUser={uploadUser}
        isAdmin={true}
      />

      <Typography variant="body1" mb={3}>
        Upload History
      </Typography>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead sx={{ backgroundColor: "#F0F4F8" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Uploaded by</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow sx={{ pt: 2, pb: 2 }}>
                <TableCell align="center" colSpan={6}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            )}

            {error && !isLoading && (
              <TableRow>
                <TableCell align="center" colSpan={6}>
                  <Box pt={1} pb={1}>
                    <Typography variant="body1" align="center">
                      Failed to fetch memory books
                    </Typography>
                    <Box display={"flex"} justifyContent={"center"} mt={1}>
                      <Button
                        onClick={fetchMemoryBooks}
                        variant="contained"
                        sx={{ ml: "auto", mr: "auto" }}
                      >
                        Retry
                      </Button>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {!error && !isLoading && memoryBooks.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" padding="normal">
                  No memory books available at the moment
                </TableCell>
              </TableRow>
            )}

            {!error &&
              !isLoading &&
              memoryBooks.length > 0 &&
              memoryBooks.map((memoryBook) => {
                return (
                  <MemoryBookTableRow
                    key={memoryBook.id}
                    memoryBook={memoryBook}
                  />
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
